import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { api } from '../api/client.js';

type SearchResult = {
  id: string;
  type: 'project' | 'resource';
  title: string;
  subtitle: string;
  href: string;
};
const searchResponseSchema = z.object({
  data: z.array(
    z.object({
      id: z.string().min(1),
      type: z.enum(['project', 'resource']),
      title: z.string(),
      subtitle: z.string(),
      href: z.string().startsWith('/'),
    }),
  ),
});

export function GlobalSearch() {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const results = useQuery({
    queryKey: ['global-search', query],
    queryFn: () =>
      api<{ data: SearchResult[] }>(
        `/api/v1/workspace/search?q=${encodeURIComponent(query.trim())}`,
        {},
        searchResponseSchema,
      ),
    enabled: query.trim().length >= 2,
  });
  return (
    <div className="global-search">
      <input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={t('shell.searchPlaceholder')}
        aria-label={t('shell.searchLabel')}
      />
      {query.trim().length >= 2 && (
        <div className="global-search-results">
          {results.isLoading && <span role="status">{t('shell.searchLoading')}</span>}
          {results.data?.data.map((result) => (
            <Link key={`${result.type}:${result.id}`} to={result.href} onClick={() => setQuery('')}>
              <strong>{result.title}</strong>
              <small>{result.subtitle}</small>
            </Link>
          ))}
          {results.data?.data.length === 0 && <span>{t('shell.searchEmpty')}</span>}
          {results.error && <span role="alert">{results.error.message}</span>}
        </div>
      )}
    </div>
  );
}
